import { useEffect, useState } from "react";
import { LogOut, Plus, Pencil, Trash2, Eye, EyeOff, X, LayoutDashboard, Building, Inbox } from "lucide-react";
import api from "@/api";

const TOKEN_KEY = "iim_admin_token";
const TYPE_LABELS = { beachfront: "Fronte mare", land: "Terreni", house: "Case", villa: "Ville", hospitality: "Strutture turistiche", farmland: "Agricolo" };
const ZONE_OPTIONS = ["nosy-be", "sainte-marie", "diego-suarez", "antananarivo", "tamatave", "tulear", "fort-dauphin", "morondava", "altopiani"];

function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      onLogin(data.token);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Errore di accesso");
    } finally { setLoading(false); }
  };
  return (
    <div className="min-h-screen bg-[#0E4D5C] flex items-center justify-center p-4" data-testid="admin-login-page">
      <form onSubmit={submit} className="w-full max-w-sm bg-[#F6EFE4] rounded-3xl p-8 shadow-2xl" data-testid="admin-login-form">
        <h1 className="font-serif text-2xl text-[#0E4D5C]">Pannello admin</h1>
        <p className="text-sm text-[#8A8F96] mt-1 mb-6">Investire in Madagascar</p>
        <label className="label-site">Email</label>
        <input type="email" className="input-site mb-4" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="admin-email-input" />
        <label className="label-site">Password</label>
        <input type="password" className="input-site mb-5" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="admin-password-input" />
        {error && <p className="text-sm text-[#B5472B] mb-4" data-testid="admin-login-error">{error}</p>}
        <button type="submit" disabled={loading} className="btn-primary w-full" data-testid="admin-login-submit">{loading ? "Accesso..." : "Accedi"}</button>
      </form>
    </div>
  );
}

const EMPTY_LISTING = {
  ref_code: "", slug: "", status: "available", type: "land", zone: "nosy-be",
  price_eur: "", price_on_request: false, surface_m2: "", built_m2: "", lat: "", lng: "",
  sea_distance_m: "", airport_km: "", featured: false, demo: true,
  title_it: "", title_en: "", description_it: "", description_en: "",
  right_type_it: "", right_type_en: "", title_status_it: "", title_status_en: "",
  risks_it: "", risks_en: "", checks_it: "", checks_en: "", tags_it: "", tags_en: "", services_it: "", services_en: "",
};

function ListingForm({ initial, onSave, onClose }) {
  const [f, setF] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true); setError("");
    const lines = (s) => s.split("\n").map((x) => x.trim()).filter(Boolean);
    const csv = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);
    const payload = {
      ref_code: f.ref_code, slug: f.slug, status: f.status, type: f.type, zone: f.zone,
      price_eur: Number(f.price_eur) || 0, price_on_request: !!f.price_on_request,
      surface_m2: Number(f.surface_m2) || 0, built_m2: f.built_m2 ? Number(f.built_m2) : null,
      lat: Number(f.lat) || 0, lng: Number(f.lng) || 0,
      sea_distance_m: f.sea_distance_m !== "" ? Number(f.sea_distance_m) : null,
      airport_km: f.airport_km !== "" ? Number(f.airport_km) : null,
      featured: !!f.featured, demo: !!f.demo,
      title: { it: f.title_it, en: f.title_en },
      description: { it: f.description_it, en: f.description_en },
      right_type: { it: f.right_type_it, en: f.right_type_en },
      title_status: { it: f.title_status_it, en: f.title_status_en },
      risks: { it: f.risks_it, en: f.risks_en },
      checks: { it: lines(f.checks_it), en: lines(f.checks_en) },
      tags: { it: csv(f.tags_it), en: csv(f.tags_en) },
      services: { it: csv(f.services_it), en: csv(f.services_en) },
      images: f.images || [],
    };
    try {
      if (f.id) await api.put(`/listings/${f.id}`, payload);
      else await api.post("/listings", payload);
      onSave();
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Errore nel salvataggio");
    } finally { setSaving(false); }
  };
  const bi = (key, label, area = false) => (
    <div className="grid sm:grid-cols-2 gap-4">
      {["it", "en"].map((l) => (
        <div key={l}>
          <label className="label-site">{label} ({l.toUpperCase()})</label>
          {area
            ? <textarea rows={3} className="input-site" value={f[`${key}_${l}`]} onChange={(e) => set(`${key}_${l}`, e.target.value)} data-testid={`form-${key}-${l}`} />
            : <input className="input-site" value={f[`${key}_${l}`]} onChange={(e) => set(`${key}_${l}`, e.target.value)} data-testid={`form-${key}-${l}`} />}
        </div>
      ))}
    </div>
  );
  return (
    <div className="fixed inset-0 z-50 bg-[#1F2328]/60 backdrop-blur-sm flex items-start justify-center p-4 overflow-auto" onClick={onClose} data-testid="listing-form-modal">
      <form onSubmit={submit} className="bg-[#F6EFE4] rounded-3xl p-6 md:p-8 max-w-3xl w-full my-8 space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="font-serif text-2xl text-[#0E4D5C]">{f.id ? "Modifica immobile" : "Nuovo immobile"}</h2>
          <button type="button" onClick={onClose} aria-label="Chiudi" data-testid="listing-form-close"><X size={20} /></button>
        </div>
        <div className="grid sm:grid-cols-3 gap-4">
          <div><label className="label-site">Codice (IM-XX-0000) *</label><input className="input-site" value={f.ref_code} onChange={(e) => set("ref_code", e.target.value)} required data-testid="form-ref-code" /></div>
          <div><label className="label-site">Slug URL *</label><input className="input-site" value={f.slug} onChange={(e) => set("slug", e.target.value)} required data-testid="form-slug" /></div>
          <div><label className="label-site">Stato</label>
            <select className="input-site" value={f.status} onChange={(e) => set("status", e.target.value)} data-testid="form-status">
              <option value="available">Disponibile</option><option value="reserved">Riservato</option><option value="sold">Venduto</option>
            </select>
          </div>
        </div>
        <div className="grid sm:grid-cols-3 gap-4">
          <div><label className="label-site">Tipologia</label>
            <select className="input-site" value={f.type} onChange={(e) => set("type", e.target.value)} data-testid="form-type">
              {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div><label className="label-site">Zona</label>
            <select className="input-site" value={f.zone} onChange={(e) => set("zone", e.target.value)} data-testid="form-zone">
              {ZONE_OPTIONS.map((z) => <option key={z} value={z}>{z}</option>)}
            </select>
          </div>
          <div><label className="label-site">Prezzo €</label><input type="number" className="input-site" value={f.price_eur} onChange={(e) => set("price_eur", e.target.value)} data-testid="form-price" /></div>
        </div>
        <div className="grid sm:grid-cols-4 gap-4">
          <div><label className="label-site">Superficie m²</label><input type="number" className="input-site" value={f.surface_m2} onChange={(e) => set("surface_m2", e.target.value)} data-testid="form-surface" /></div>
          <div><label className="label-site">Sup. costruita m²</label><input type="number" className="input-site" value={f.built_m2} onChange={(e) => set("built_m2", e.target.value)} /></div>
          <div><label className="label-site">Lat</label><input type="number" step="any" className="input-site" value={f.lat} onChange={(e) => set("lat", e.target.value)} /></div>
          <div><label className="label-site">Lng</label><input type="number" step="any" className="input-site" value={f.lng} onChange={(e) => set("lng", e.target.value)} /></div>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><label className="label-site">Distanza mare (m)</label><input type="number" className="input-site" value={f.sea_distance_m} onChange={(e) => set("sea_distance_m", e.target.value)} /></div>
          <div><label className="label-site">Distanza aeroporto (km)</label><input type="number" step="any" className="input-site" value={f.airport_km} onChange={(e) => set("airport_km", e.target.value)} /></div>
        </div>
        <div className="flex flex-wrap gap-6">
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!f.price_on_request} onChange={(e) => set("price_on_request", e.target.checked)} className="h-4 w-4 accent-[#B5472B]" /> Prezzo su richiesta</label>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!f.featured} onChange={(e) => set("featured", e.target.checked)} className="h-4 w-4 accent-[#B5472B]" data-testid="form-featured" /> In evidenza</label>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!f.demo} onChange={(e) => set("demo", e.target.checked)} className="h-4 w-4 accent-[#B5472B]" /> Etichetta "Esempio"</label>
        </div>
        {bi("title", "Titolo")}
        {bi("description", "Descrizione", true)}
        {bi("right_type", "Tipo di diritto")}
        {bi("title_status", "Stato del titolo")}
        {bi("risks", "Rischi noti", true)}
        {bi("checks", "Verifiche (una per riga)", true)}
        {bi("tags", "Tag (separati da virgola)")}
        {bi("services", "Servizi (separati da virgola)")}
        {error && <p className="text-sm text-[#B5472B]" data-testid="listing-form-error">{error}</p>}
        <button type="submit" disabled={saving} className="btn-primary w-full" data-testid="listing-form-save">{saving ? "Salvataggio..." : "Salva immobile"}</button>
      </form>
    </div>
  );
}

function ListingsTab() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/listings").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const toForm = (l) => ({
    ...EMPTY_LISTING, id: l.id, ref_code: l.ref_code, slug: l.slug, status: l.status, type: l.type, zone: l.zone,
    price_eur: l.price_eur, price_on_request: l.price_on_request, surface_m2: l.surface_m2, built_m2: l.built_m2 ?? "",
    lat: l.lat, lng: l.lng, sea_distance_m: l.sea_distance_m ?? "", airport_km: l.airport_km ?? "",
    featured: l.featured, demo: l.demo, images: l.images || [],
    title_it: l.title?.it || "", title_en: l.title?.en || "",
    description_it: l.description?.it || "", description_en: l.description?.en || "",
    right_type_it: l.right_type?.it || "", right_type_en: l.right_type?.en || "",
    title_status_it: l.title_status?.it || "", title_status_en: l.title_status?.en || "",
    risks_it: l.risks?.it || "", risks_en: l.risks?.en || "",
    checks_it: (l.checks?.it || []).join("\n"), checks_en: (l.checks?.en || []).join("\n"),
    tags_it: (l.tags?.it || []).join(", "), tags_en: (l.tags?.en || []).join(", "),
    services_it: (l.services?.it || []).join(", "), services_en: (l.services?.en || []).join(", "),
  });
  const del = async (l) => {
    if (!window.confirm(`Eliminare ${l.ref_code}?`)) return;
    await api.delete(`/listings/${l.id}`);
    load();
  };
  return (
    <div data-testid="admin-listings-tab">
      <div className="flex justify-between items-center mb-5">
        <h2 className="font-serif text-xl text-[#0E4D5C]">Immobili ({items.length})</h2>
        <button onClick={() => setEditing({ ...EMPTY_LISTING })} className="btn-primary !py-2 !px-4 !text-sm" data-testid="admin-new-listing"><Plus size={15} /> Nuovo</button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[#8A8F96] border-b border-[#0E4D5C]/10">
              <th className="py-2 pr-4">Codice</th><th className="py-2 pr-4">Titolo</th><th className="py-2 pr-4">Tipo</th><th className="py-2 pr-4">Stato</th><th className="py-2 pr-4">Prezzo</th><th className="py-2">Azioni</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#0E4D5C]/10">
            {items.map((l) => (
              <tr key={l.id} data-testid={`admin-listing-row-${l.ref_code}`}>
                <td className="py-3 pr-4 font-medium">{l.ref_code}</td>
                <td className="py-3 pr-4">{l.title?.it}</td>
                <td className="py-3 pr-4">{TYPE_LABELS[l.type]}</td>
                <td className="py-3 pr-4">{l.status}</td>
                <td className="py-3 pr-4">{l.price_eur?.toLocaleString("it-IT")} €</td>
                <td className="py-3 flex gap-2">
                  <button onClick={() => setEditing(toForm(l))} className="p-2 rounded-lg bg-[#0E4D5C]/10 text-[#0E4D5C] hover:bg-[#0E4D5C]/20" aria-label="Modifica" data-testid={`admin-edit-${l.ref_code}`}><Pencil size={14} /></button>
                  <button onClick={() => del(l)} className="p-2 rounded-lg bg-[#B5472B]/10 text-[#B5472B] hover:bg-[#B5472B]/20" aria-label="Elimina" data-testid={`admin-delete-${l.ref_code}`}><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editing && <ListingForm initial={editing} onSave={() => { setEditing(null); load(); }} onClose={() => setEditing(null)} />}
    </div>
  );
}

const LEAD_TYPE_LABELS = { contact: "Richiesta info", guide: "Guida gratuita", newsletter: "Newsletter", sell: "Vendi con noi" };

function LeadsTab() {
  const [leads, setLeads] = useState([]);
  const [filter, setFilter] = useState("");
  const load = () => api.get("/leads", { params: filter ? { type: filter } : {} }).then((r) => setLeads(r.data)).catch(() => {});
  useEffect(() => { load(); }, [filter]);
  const markRead = async (l) => { await api.patch(`/leads/${l.id}`, { read: !l.read }); load(); };
  const del = async (l) => { if (window.confirm("Eliminare il contatto?")) { await api.delete(`/leads/${l.id}`); load(); } };
  return (
    <div data-testid="admin-leads-tab">
      <div className="flex flex-wrap justify-between items-center gap-3 mb-5">
        <h2 className="font-serif text-xl text-[#0E4D5C]">Contatti ({leads.length})</h2>
        <select className="input-site !w-auto !py-2" value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="admin-leads-filter">
          <option value="">Tutti i tipi</option>
          {Object.entries(LEAD_TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>
      <div className="space-y-3">
        {leads.length === 0 && <p className="text-sm text-[#8A8F96]">Nessun contatto per ora.</p>}
        {leads.map((l) => (
          <div key={l.id} className={`card-site p-5 ${l.read ? "opacity-60" : ""}`} data-testid={`lead-${l.id}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-3">
                <span className="rounded-full bg-[#B5472B]/10 text-[#B5472B] text-[11px] font-semibold uppercase tracking-wider px-2.5 py-1">{LEAD_TYPE_LABELS[l.type] || l.type}</span>
                <span className="font-medium text-sm">{l.name || "—"} · {l.email}</span>
                <span className="text-xs text-[#8A8F96] uppercase">{l.lang}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-[#8A8F96]">{new Date(l.created_at).toLocaleString("it-IT")}</span>
                <button onClick={() => markRead(l)} className="p-2 rounded-lg bg-[#0E4D5C]/10 text-[#0E4D5C]" aria-label="Segna letto" data-testid={`lead-read-${l.id}`}>{l.read ? <EyeOff size={14} /> : <Eye size={14} />}</button>
                <button onClick={() => del(l)} className="p-2 rounded-lg bg-[#B5472B]/10 text-[#B5472B]" aria-label="Elimina"><Trash2 size={14} /></button>
              </div>
            </div>
            {(l.phone || l.country || l.budget || l.timeline) && (
              <p className="mt-2 text-xs text-[#8A8F96]">{[l.phone, l.country, l.budget, l.timeline, l.contact_pref].filter(Boolean).join(" · ")}</p>
            )}
            {l.property_ref && <p className="mt-1 text-xs font-medium text-[#0E4D5C]">Immobile: {l.property_ref} — {l.property_title}</p>}
            {l.message && <p className="mt-2 text-sm text-[#1F2328]/75 leading-relaxed">{l.message}</p>}
            {l.newsletter && <p className="mt-1 text-xs text-[#2FA3A0]">Iscritto anche alla newsletter</p>}
          </div>
        ))}
      </div>
    </div>
  );
}

function OverviewTab() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {}); }, []);
  if (!stats) return <div className="h-40 rounded-2xl bg-[#0E4D5C]/5 animate-pulse" />;
  const cards = [
    ["Immobili pubblicati", stats.listings_total], ["Disponibili", stats.listings_available],
    ["Contatti totali", stats.leads_total], ["Contatti da leggere", stats.leads_unread],
  ];
  return (
    <div data-testid="admin-overview-tab">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map(([label, val]) => (
          <div key={label} className="card-site p-6" data-testid={`stat-${label}`}>
            <p className="font-serif text-4xl text-[#B5472B]">{val}</p>
            <p className="text-sm text-[#1F2328]/60 mt-1">{label}</p>
          </div>
        ))}
      </div>
      <h3 className="font-serif text-lg text-[#0E4D5C] mt-8 mb-3">Per tipologia</h3>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {Object.entries(stats.by_type).map(([k, v]) => (
          <div key={k} className="card-site p-4 text-center">
            <p className="font-serif text-2xl text-[#0E4D5C]">{v}</p>
            <p className="text-xs text-[#8A8F96] mt-1">{TYPE_LABELS[k]}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY));
  const [tab, setTab] = useState("overview");
  useEffect(() => {
    if (token) api.get("/auth/me").catch(() => { localStorage.removeItem(TOKEN_KEY); setToken(null); });
  }, [token]);
  if (!token) return <Login onLogin={setToken} />;
  const tabs = [["overview", "Panoramica", LayoutDashboard], ["listings", "Immobili", Building], ["leads", "Contatti", Inbox]];
  return (
    <div className="min-h-screen bg-[#F6EFE4]" data-testid="admin-dashboard">
      <header className="bg-[#0E4D5C] text-[#F6EFE4]">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <h1 className="font-serif text-lg">Admin · Investire in Madagascar</h1>
          <button onClick={() => { localStorage.removeItem(TOKEN_KEY); setToken(null); }} className="flex items-center gap-2 text-sm text-[#F6EFE4]/80 hover:text-white" data-testid="admin-logout">
            <LogOut size={15} /> Esci
          </button>
        </div>
      </header>
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex gap-2 mb-8">
          {tabs.map(([k, label, Icon]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`admin-tab-${k}`}
              className={`flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium transition-colors ${tab === k ? "bg-[#B5472B] text-white" : "bg-[#0E4D5C]/5 text-[#0E4D5C]"}`}>
              <Icon size={15} /> {label}
            </button>
          ))}
        </div>
        {tab === "overview" && <OverviewTab />}
        {tab === "listings" && <ListingsTab />}
        {tab === "leads" && <LeadsTab />}
      </div>
    </div>
  );
}
